import type { Price } from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import type { Except } from "type-fest";
import { z } from "zod";
import type { DurationInHours } from "../date-time/time-spans";
import { durationInHoursZodSchema } from "../date-time/time-spans";
import type { Amount } from "../units-and-quantities/amount";
import { amountZodSchema } from "../units-and-quantities/amount";
import type { ContentBlock } from "./content-block";
import { contentBlockZodSchema } from "./content-block";

type Voucher = {
    readonly _type: "VOUCHER";
    readonly price: Price;
    /**
     * Amount of stock item required for 1 sku \
     *
     * E.g. A shirt could have 5 pcs in stock \
     * A sku could be a two-pack of shirts. \
     * When you buy 1 of the sku, the amount (below) is 2 \
     * Two shirts in stock is required to buy one of the sku
     * @type {Amount}
     */
    readonly amount: Amount;
    readonly valid: DurationInHours;
    readonly contentBlocks: ContentBlock[];

    /**
     * If true, the vouchers will not generate tickets. Shortterm solution \
     * for the case when we want to sell vouchers but not tickets. \
     * This is a temporary solution and should be removed in the future.
     * In favor for real digital products.
     * @type {boolean}
     */
    readonly disableTicketsGeneration?: boolean;
};

type FactoryParams = Except<Voucher, "_type">;
const voucherFactory = (params: FactoryParams): Voucher => {
    const { price, valid, amount, contentBlocks } = params;
    return {
        _type: "VOUCHER",
        price,
        valid,
        amount,
        contentBlocks,
    };
};

const voucherZodSchema: z.ZodSchema<Voucher> = z.object({
    _type: z.literal("VOUCHER"),
    price: priceZodSchema,
    amount: amountZodSchema,
    valid: durationInHoursZodSchema,
    contentBlocks: z.array(contentBlockZodSchema),
    disableTicketsGeneration: z.boolean().optional(),
});

export { voucherFactory, voucherZodSchema as voucherZodObject };
export type { Voucher };
